"use client";

import { useCallback, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useUserLocation } from "@/hooks/location/useUserLocation";
import { getFcmToken } from "@/lib/permissions/notification";
import { getDeviceInfo } from "@/lib/device/getDeviceInfo";
import CreatePasswordContent from "./CreatePasswordContent";
import { useRegister } from "@/hooks/auth/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordFormSchema, RegisterParamSchema } from "@/types/auth";

type CreatePasswordFormFields = {
  password: string;
  confirmPassword: string;
};

const AUTO_SIGN_IN_FAILED_CODE = "WEB_SESSION_MISSING";

const buildRegisterPayload = (params: {
  phone: string;
  password: string;
  verificationToken: string;
  fcmToken: string;
  deviceInfo: ReturnType<typeof getDeviceInfo>;
  userLocation?: { lat?: number; lng?: number } | null;
}) => {
  return RegisterParamSchema.parse({
    countryCode: "+855",
    phone: params.phone.replace(/\D/g, ""),
    password: params.password,
    otpToken: params.verificationToken,
    fcmToken: params.fcmToken,
    model: params.deviceInfo.model,
    systemVersion: params.deviceInfo.systemVersion,
    isEmulator: params.deviceInfo.isEmulator,
    lat: params.userLocation?.lat?.toString() || "",
    lng: params.userLocation?.lng?.toString() || "",
  });
};

const CreatePasswordStep = () => {
  const { phone, verificationToken, completeAuthSuccess, openPhoneLogin } =
    useAuthModal();
  const { data: userLocation } = useUserLocation();

  const methods = useForm<CreatePasswordFormFields>({
    resolver: zodResolver(PasswordFormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const { handleSubmit, watch, setError, clearErrors } = methods;

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");

  // Guard: if phone or verification token is missing, redirect to phone login
  useEffect(() => {
    if (phone && verificationToken) return;
    void openPhoneLogin(phone);
  }, [openPhoneLogin, phone, verificationToken]);

  const { mutate: register, isPending: isRegistering } = useRegister({
    onSuccess: () => {
      void completeAuthSuccess();
    },
    onError: (message, code) => {
      if (code === AUTO_SIGN_IN_FAILED_CODE) {
        void openPhoneLogin(phone);
        return;
      }

      setError("password", {
        type: "server",
        message: message || "Failed to create account",
      });
    },
  });

  const onSubmit = useCallback(
    async (data: CreatePasswordFormFields) => {
      if (!phone || !verificationToken || isRegistering) return;

      clearErrors();

      try {
        const [fcmToken, deviceInfo] = await Promise.all([
          getFcmToken().catch(() => ""),
          Promise.resolve(getDeviceInfo()),
        ]);

        const payload = buildRegisterPayload({
          phone,
          password: data.password,
          verificationToken,
          fcmToken: fcmToken || "",
          deviceInfo,
          userLocation,
        });

        register(payload);
      } catch (error) {
        console.error("Failed to register:", error);
        setError("password", {
          type: "server",
          message: "Something went wrong. Please try again.",
        });
      }
    },
    [
      clearErrors,
      isRegistering,
      phone,
      register,
      setError,
      userLocation,
      verificationToken,
    ],
  );

  return (
    <FormProvider {...methods}>
      <CreatePasswordContent
        passwordValue={passwordValue}
        confirmPasswordValue={confirmPasswordValue}
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isRegistering}
      />
    </FormProvider>
  );
};

export default CreatePasswordStep;
